import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import {
  TenantCollection,
  TenantMembership,
  TenantLinkRequest,
  TenantLinkRequestView,
  TenantMembershipView,
} from '../types/tenancy';

export const tenantPath = (
  teacherId: string,
  collectionName: TenantCollection
): string => `teachers/${teacherId}/${collectionName}`;

export const tenantCollection = (
  teacherId: string,
  collectionName: TenantCollection
) => collection(db, tenantPath(teacherId, collectionName));

export const tenantDocument = (
  teacherId: string,
  collectionName: TenantCollection,
  documentId: string
) => doc(db, tenantPath(teacherId, collectionName), documentId);

class TenantService {
  async getMemberships(userId: string): Promise<TenantMembership[]> {
    const snapshot = await getDocs(
      query(
        collection(db, 'users', userId, 'memberships'),
        where('status', '==', 'active')
      )
    );

    return snapshot.docs.map(item => ({
      teacherId: item.id,
      ...item.data(),
    })) as TenantMembership[];
  }

  async getMembershipViews(userId: string): Promise<TenantMembershipView[]> {
    const memberships = await this.getMemberships(userId);
    return Promise.all(
      memberships.map(async membership => {
        const teacherSnapshot = await getDoc(
          doc(db, 'users', membership.teacherId)
        );
        const teacherData = teacherSnapshot.exists()
          ? teacherSnapshot.data()
          : {};

        return {
          ...membership,
          teacherName:
            teacherData.name ||
            teacherData.displayName ||
            teacherData.email ||
            membership.teacherId,
          teacherEmail: teacherData.email || '',
        };
      })
    );
  }

  async resolvePrimaryTeacherId(
    userId: string,
    role: string
  ): Promise<string | null> {
    if (role === 'teacher') return userId;

    const primarySnapshot = await getDocs(
      query(
        collection(db, 'users', userId, 'memberships'),
        where('status', '==', 'active'),
        where('isPrimary', '==', true),
        limit(1)
      )
    );

    if (!primarySnapshot.empty) return primarySnapshot.docs[0].id;

    const memberships = await this.getMemberships(userId);
    return memberships[0]?.teacherId || null;
  }

  async createLinkRequest(input: {
    teacherId?: string;
    code: string;
    message?: string;
  }): Promise<{ requestId: string }> {
    const callable = httpsCallable<
      typeof input,
      { requestId: string }
    >(functions, 'createTenantLinkRequest');
    const result = await callable(input);
    return result.data;
  }

  async reviewLinkRequest(input: {
    teacherId: string;
    requestId: string;
    decision: 'approved' | 'rejected';
    reason?: string;
  }): Promise<void> {
    const callable = httpsCallable<typeof input, { success: boolean }>(
      functions,
      'reviewTenantLinkRequest'
    );
    await callable(input);
  }

  async unlinkParent(input: {
    teacherId: string;
    studentId: string;
    reason?: string;
  }): Promise<void> {
    const callable = httpsCallable<typeof input, { success: boolean }>(
      functions,
      'unlinkTenantParent'
    );
    await callable(input);
  }

  async assignParent(input: {
    teacherId: string;
    studentId: string;
    parentId: string;
  }): Promise<void> {
    const callable = httpsCallable<typeof input, { success: boolean }>(
      functions,
      'assignTenantParent'
    );
    await callable(input);
  }

  private async getTenantUserProfile(
    teacherId: string,
    collectionName: 'students' | 'parents',
    userId?: string
  ): Promise<{ name: string; email?: string } | null> {
    if (!userId) return null;

    const tenantSnapshot = await getDoc(
      tenantDocument(teacherId, collectionName, userId)
    );
    if (tenantSnapshot.exists()) {
      const data = tenantSnapshot.data();
      return {
        name: data.name || data.displayName || userId,
        email: data.email || '',
      };
    }

    const userSnapshot = await getDoc(doc(db, 'users', userId));
    if (!userSnapshot.exists()) return null;
    const data = userSnapshot.data();
    return {
      name: data.name || data.displayName || data.email || userId,
      email: data.email || '',
    };
  }

  async getLinkRequests(teacherId: string): Promise<TenantLinkRequestView[]> {
    const snapshot = await getDocs(tenantCollection(teacherId, 'linkRequests'));
    const requests = snapshot.docs.map(item => ({
      id: item.id,
      ...item.data(),
    })) as TenantLinkRequest[];

    return Promise.all(
      requests.map(async request => {
        const [student, parent, requester, transferTeacher] = await Promise.all([
          this.getTenantUserProfile(teacherId, 'students', request.studentId),
          this.getTenantUserProfile(teacherId, 'parents', request.parentId),
          this.getTenantUserProfile(
            teacherId,
            request.requesterRole === 'parent' ? 'parents' : 'students',
            request.requesterId
          ),
          request.transferFromTeacherId
            ? getDoc(doc(db, 'users', request.transferFromTeacherId))
            : Promise.resolve(null),
        ]);
        const transferTeacherData =
          transferTeacher && transferTeacher.exists()
            ? transferTeacher.data()
            : null;

        return {
          ...request,
          requestType: request.requestType || 'parent_link',
          studentName: student?.name || request.studentId,
          studentEmail: student?.email,
          parentName: parent?.name,
          parentEmail: parent?.email,
          requesterName: requester?.name || request.requesterId,
          transferFromTeacherName:
            transferTeacherData?.name ||
            transferTeacherData?.displayName ||
            transferTeacherData?.email ||
            request.transferFromTeacherId,
        };
      })
    );
  }
}

export const tenantService = new TenantService();
export default tenantService;
